import { useCallback, useMemo, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { FileText, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { Insured, Policy } from "@shared/schema";
import { PoliciesTable } from "../policies/PoliciesTable";
import { sortPoliciesByEffectiveDate } from "../policies/policy-status";
import { InsuredPolicyDetail } from "../policies/InsuredPolicyDetail";
import { AddPolicyDialog } from "@/components/insured/policies/AddPolicyDialog";

interface Props {
  insured: Insured;
  policies: Policy[];
  // CSR-2327 — the AOR experience flag, read once by the insured page.
  showAor?: boolean;
}

export function PoliciesTab({ insured, policies, showAor = false }: Props) {
  const [location, navigate] = useLocation();
  const search = useSearch();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const selectedPolicyId = searchParams.get("policyId");
  const [addOpen, setAddOpen] = useState(false);

  const sortedPolicies = useMemo(
    () => sortPoliciesByEffectiveDate(policies),
    [policies]
  );

  const selectedPolicy = useMemo(
    () => sortedPolicies.find((p) => p.id === selectedPolicyId),
    [sortedPolicies, selectedPolicyId]
  );

  const updatePolicyId = useCallback(
    (policyId: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (policyId) {
        next.set("policyId", policyId);
      } else {
        next.delete("policyId");
      }
      const queryString = next.toString();
      navigate(queryString ? `${location}?${queryString}` : location, {
        replace: false,
      });
    },
    [location, navigate, searchParams]
  );

  const handleSelect = useCallback(
    (policy: Policy) => updatePolicyId(policy.id),
    [updatePolicyId]
  );

  const handleBack = useCallback(() => updatePolicyId(null), [updatePolicyId]);

  if (selectedPolicyId) {
    return (
      <InsuredPolicyDetail
        policyId={selectedPolicyId}
        insured={insured}
        fallbackPolicy={selectedPolicy}
        onBack={handleBack}
        showAor={showAor}
      />
    );
  }

  return (
    <Card className="overflow-hidden" data-testid="policies-tab">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-foreground" aria-hidden="true" />
          <h3 className="text-base font-semibold">Policies</h3>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-muted text-xs font-medium text-muted-foreground"
            data-testid="policies-count"
          >
            {sortedPolicies.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full gap-1.5 cursor-pointer"
            data-testid="policies-add"
            onClick={() => setAddOpen(true)}
          >
            <Plus className="w-4 h-4" /> Add Policy
          </Button>
        </div>
      </div>
      {sortedPolicies.length === 0 ? (
        <div
          className="px-6 py-12 text-center text-sm text-muted-foreground"
          data-testid="policies-empty"
        >
          No policies for this insured.
        </div>
      ) : (
        <PoliciesTable
          policies={sortedPolicies}
          onSelect={handleSelect}
          showAor={showAor}
        />
      )}
      <AddPolicyDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        insuredId={insured.id}
        customerName={insured.name ?? ""}
      />
    </Card>
  );
}
